import { useState, useMemo, createContext, useContext, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid';
import { timeLineAccumulator } from './timelineUtils';
import useLocalStorage from './useLocalStorage';
import { appContext } from './AppContext';
import { useLocation } from 'react-router-dom';
import { getBirthdayMonthsOffest } from './dateUtil';
import { newPlanRow, kidRowsAllValid, kidHasCompleteRow, rowMaxUnits, normalizeKid, resolveKidPlans, kidNeedsPlanResolve, groupLinesToKids } from './planUtils';
// DEV STUB
// import plans from './db.json'
// import stub from './stub.json'
// import { useFetch } from './useFetch';


// Genarate Context Provider
export const simulatorContext = createContext();

const initialKid = { name: '', idType: 'Person_Id', personId: '', passportId: '', age: null, marriage: null, preBorn: false, birthday: '', }

export const familyPlanReturnMap = { 1: 0, 2: 0, 3: 0.75, 4: 0.76, 5: 0.78, 6: 0.75, 7: 0.76 }

export const monthName = ["ינו'", "פבר'", "מרץ", "אפר'", "מאי", "יוני", "יולי", "אוג'", "ספט'", "אוק'", "נוב'", "דצמ'"];
export const monthFullName = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

// Display headers/labels per age bracket - plan AVAILABILITY is DB driven (minAgeMonths/maxAgeMonths)
export const planAgeMapper = {
  0: {
    ageLabel: "זוגות צעירים לאחר הנישואין ועד חצי שנה מהלידה",
    header: 'תכנית הצעירים',
  },
  0.5: {
    ageLabel: "זוגות צעירים לאחר הנישואין ועד חצי שנה מהלידה",
    header: 'תכנית הצעירים',
  },
  1: {
    ageLabel: "גיל 0-3",
    header: 'התוכנית המרכזית',
  },
  2: {
    ageLabel: "גיל 0-3",
    header: 'התוכנית המרכזית',
  },
  3: {
    ageLabel: "גיל 0-3",
    header: 'התוכנית המרכזית',
  },
  default: {
    ageLabel: "גיל 3-16",
    header: 'תוכנית רב גילאית',
  },

}


export default function SimulatorContext({ children }) {
  // Donation / Retuns Tab state managment
  const [tab, setTab] = useState(0);

  const { user, navigate, setUser, urlAgent, data, plans } = useContext(appContext)

  const [stockMarket, setStockMarket] = useState({ enabled: urlAgent.stockMarket, loanInterest: 0, marketInterest: 0, managementFee: 0 });


  const [userNamePopup, setUserNamePopup] = useState(false);

  // // Fetch plans from Backend
  // const { data: plans } = useFetch('/plans');


  const location = useLocation();

  const [kids, setKids, attemptUseStoredValue] = useLocalStorage(user?.email, [{ ...initialKid, plans: [newPlanRow()], id: uuidv4() }]);

  // Migrate legacy single-plan kids & re-resolve stored plan objects with fresh DB fields (minAgeMonths/maxAgeMonths)
  useEffect(() => {
    if (!plans?.length || !kids?.some(kidNeedsPlanResolve)) return
    setKids(prev => prev?.map(kid => resolveKidPlans(normalizeKid(kid), plans)))
  }, [plans, kids, setKids])

  const orderdKids = [];

  // Case no user - Check for location state user & local storage kids
  useEffect(() => {
    if (!user.email || !user.firstName || !user.lastName) {
      if (location?.state?.user) {
        setUser(location.state.user)
        if (location.state.user?.email)
          attemptUseStoredValue(location.state.user.email)

      }
      // Case no location state - check if Agent or continue Lead
      else if (!urlAgent.agent && !urlAgent.mainAgent && !urlAgent.lead && !urlAgent.donor)
        navigate('/')
    }
  }, [user, navigate, location, setUser, attemptUseStoredValue, urlAgent]);

  // Update kids on continue Lead kids - group flat SF lines into kids with plan rows
  useEffect(() => {
    if (data?.lines)
      setKids(groupLinesToKids(data.lines, plans, initialKid))

  }, [data, plans, setKids])


  // A child's ת.ז is optional, but a filled-and-invalid one blocks proceeding (like the lead page)
  const someChildIdInvalid = kids?.some(kid => kid.personId && kid.personIdValidation?.invalid);

  // Handle add kid button state
  const addKidDisabled = someChildIdInvalid || kids?.some(kid => !(kid.name && kid.age && kid.marriage && kidRowsAllValid(kid)));

  // Handle Add kid button action
  const handleAddKid = () => {
    setKids(prev => [...prev, { ...initialKid, plans: [newPlanRow()], id: uuidv4() }])
  };
  // Handle reset kids cards
  const handleResetKids = () => {
    setKids([{ ...initialKid, plans: [newPlanRow()], id: uuidv4() }])
  };

  // Flatten kids plan rows into one entry per (kid, plan) - keeps single-plan shape for all downstream consumers
  const flattenedKids = kids?.map(kid => kid).sort((a, b) => b.age - a.age)?.flatMap(kid =>
    (kid.age !== null && kid.marriage) ?
      (kid.plans ?? []).filter(row => row.plan && row.units > 0).map(row => ({
        ...kid,
        id: `${kid.id}:${row.plan.route}`,
        kidId: kid.id,
        plan: row.plan,
        units: row.units,
        ...((row.requestLine) ? { requestLine: row.requestLine } : {}),
        plans: undefined,
      })) : []) ?? [];

  // Summary kids
  const completedKids = flattenedKids.reduce((acc, kid) => {
    if (kid.age !== null && kid.plan && kid.marriage && kid?.units > 0) {
      let modifyKid = JSON.parse(JSON.stringify(kid));

      if (kid.plan.totalPay === kid.plan.return) {
        //callculate kid grant  for rest of units (50 /66 %)
        modifyKid.plan.return = (kid.plan.monthsSave >= 192) ? kid.plan.return * 0.66 : kid.plan.return * 0.5;
        //first kid get compleate unit grant
        if (!acc.grantFirstUnit) {
          modifyKid.plan.return = (kid.plan.return + modifyKid.plan.return * (kid.units - 1)) / kid.units;
          modifyKid.grantFirstUnit = true;
        }
        acc.grantFirstUnit = true;
      }
      if (stockMarket.enabled) {
        let depositYears = Math.floor(kid.plan.monthsSave / 12);
        let remainingMonths = kid.plan.monthsSave % 12;
        let MonthsOffset = getBirthdayMonthsOffest(kid.birthday, kid.age);
        let monthsWait = ((kid.marriage - kid.age) * 12 > kid.plan.monthsSave) ? (kid.marriage - kid.age) * 12 - kid.plan.monthsSave + MonthsOffset : MonthsOffset;
        let waitYears = Math.floor(monthsWait / 12);
        let remainingWaitMonths = monthsWait % 12;
        let accumulatedAmount = 0;

        for (let i = 0; i < depositYears; i++) {
          // Add deposits for the year
          accumulatedAmount += kid.plan.monthlyPay * kid.units * 12;
          // Apply annual interest
          accumulatedAmount *= (1 + stockMarket.marketInterest / 100);
          // Deduct management fee
          accumulatedAmount *= (1 - stockMarket.managementFee / 100);
        }
        if (remainingMonths > 0) {
          accumulatedAmount += kid.plan.monthlyPay * kid.units * remainingMonths;
          accumulatedAmount *= (1 + (stockMarket.marketInterest / 100) * (remainingMonths / 12));
          accumulatedAmount *= (1 - (stockMarket.managementFee / 100) * (remainingMonths / 12));
        }

        for (let i = 0; i < waitYears; i++) {
          // Apply annual interest
          accumulatedAmount *= (1 + stockMarket.marketInterest / 100);
          // Deduct management fee
          accumulatedAmount *= (1 - stockMarket.managementFee / 100);
        }
        if (remainingWaitMonths > 0) {
          accumulatedAmount *= (1 + (stockMarket.marketInterest / 100) * (remainingWaitMonths / 12));
          accumulatedAmount *= (1 - (stockMarket.managementFee / 100) * (remainingWaitMonths / 12));

        }

        let totalDeposit = kid.plan.monthlyPay * kid.units * kid.plan.monthsSave;
        let totalPrfit = accumulatedAmount - totalDeposit;

        // let monthlyProfitIntrest = stockMarket.marketInterest / 1200;
        // let stockMarketDonationPrfit = Math.round(kid.units * kid.plan.monthlyPay * ((((1 + monthlyProfitIntrest) ** kid.plan.monthsSave) - 1) / monthlyProfitIntrest));
        // let totalPrfit = (!!monthlyProfitIntrest) ? stockMarketDonationPrfit * ((1 + monthlyProfitIntrest) ** monthsWait) - totalDeposit : 0;
        let profitVat = totalPrfit * 0.25;
        let totalForWedding = totalDeposit + totalPrfit - profitVat;
        let completeWithLoan = (kid.plan.loan * kid.units) - totalForWedding;

        let monthlyLoanIntrest = stockMarket.loanInterest / 1200;
        let loanNumberPayments = Math.ceil(kid.plan.loan / kid.plan.monthlyReturn);
        let loanMonthlyPayment = (!!monthlyLoanIntrest) ?
          completeWithLoan * monthlyLoanIntrest * ((1 + monthlyLoanIntrest) ** loanNumberPayments) / ((1 + monthlyLoanIntrest) ** loanNumberPayments - 1) :
          completeWithLoan / loanNumberPayments;
        let loanPaymet = loanMonthlyPayment * loanNumberPayments;
        let loanInterest = loanPaymet - completeWithLoan;
        let totalPayment = totalDeposit + loanPaymet;
        let totalGLBpayment = kid.units * (kid.plan.totalPay + kid.plan.loan - modifyKid.plan.return);
        let totalDiff = totalPayment - totalGLBpayment
        modifyKid.stockMarket = {
          totalPrfit,
          profitVat,
          totalForWedding,
          completeWithLoan,
          loanMonthlyPayment,
          loanInterest,
          totalPayment,
          totalGLBpayment,
          totalDiff
        };
      }
      acc.kids.push(modifyKid);
    }
    return acc
  }, { kids: [], grantFirstUnit: false }).kids


  // Donations & Loans summary accumulator
  const summaryKids = completedKids?.reduce((acc, kid) => {
    acc.totalUnits += Number(kid?.units);
    acc.totalMonthlyDonation += kid?.units * kid?.plan?.monthlyPay;
    acc.totalDonation += kid?.units * kid?.plan?.totalPay;
    acc.totalReturn += kid?.units * kid?.plan?.return;
    acc.totalLoan += kid?.units * kid?.plan?.loan //units * 40000; 
    acc.familyTotalReturn += kid.familyGrant;
    acc.stockMarketDiff += kid.stockMarket?.totalDiff;
    return acc
  }, { totalUnits: 0, totalMonthlyDonation: 0, totalLoan: 0, totalDonation: 0, totalReturn: 0, familyTotalReturn: 0, stockMarketDiff: 0 });


  const timeLine = useMemo(() => timeLineAccumulator(completedKids), [completedKids]);

  // Valid kids in grouped (multi plan rows) shape - for navigation to registration
  const validKids = kids?.filter(kid => kid.age !== null && kid.marriage && kidHasCompleteRow(kid))
    .map(kid => ({ ...kid, plans: kid.plans.filter(row => row.plan && row.units > 0) }));

  // Handle summary units slider
  const HandleChangeSummaryDonationSlider = (value) => {
    let childUnits = Math.round(value / completedKids?.length);
    setKids(prev =>
      prev.map(kid => {
        if (!(kid.name && kid.age && kid?.marriage > 0 && kid.plans?.some(row => row.plan))) return kid
        const firstRow = kid.plans.find(row => row.plan)
        const maxUnits = rowMaxUnits(kid, firstRow.rowId, firstRow.plan)
        return { ...kid, plans: kid.plans.map(row => (row.rowId === firstRow.rowId) ? { ...row, units: Math.min(childUnits, maxUnits) } : row) }
      }))
  };

  const value = {
    kids, setKids,
    donor: user, // lead - so a child ת.ז equal to the lead's is flagged (like registration)
    handleResetKids, handleAddKid,
    completedKids, validKids, timeLine, addKidDisabled, someChildIdInvalid, summaryKids,
    tab, setTab,
    HandleChangeSummaryDonationSlider,
    // checkReciveGrant,
    orderdKids,
    userNamePopup, setUserNamePopup,
    stockMarket, setStockMarket
  }

  return (
    <simulatorContext.Provider value={value}>
      {children}
    </simulatorContext.Provider>
  )
}
